import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { userClient } from 'lib/clients'
import recoveryClient from 'lib/clients/recovery'
import { getMe } from 'lib/hoc/withAccessHandler'
import type { AppState } from 'lib/redux'
import { merge } from 'lodash';
import { v4 as uuid } from 'uuid';
import { TeamUser, User } from 'lib/types';

export type Me = TeamUser;

export interface AccountState {
  teams: any[],
  invites: any,
  me: Me,
  draftAccount: Me['user'],
  invoices: {
    date: string;
    id: string;
    paid: boolean;
    total: number;
    currency: string;
  }[],
}

const initialState: AccountState = {
  teams: [],
  invites: [],
  draftAccount: undefined,
  me: {
    permissions: null,
    team: {
      id: '',
      identifier: '',
      name: '',
      delete_on: null,
      delete_scheduled: false,
      owner_delete_on: null,
      owner_delete_scheduled: false,
    },
    user: {
      first_name: '',
      last_name: '',
      password: '',
      email: '',
      id: '',
      delete_scheduled: false,
      has_avatar: false,
      has_password: false,
      avatarKey: uuid(),
      key_id: null,
      private_key_armored: null,
      public_key_armored: null
    },
  },
  invoices: [],
}

export const refreshMe = createAsyncThunk(
  'account/refreshMe',
  async (baseURl: string, { dispatch }) => {
    const updatedMe = await getMe(baseURl);
    dispatch(setMe(updatedMe));
  }
);

export const updateMe = createAsyncThunk(
  'account/updateMe',
  async (user: Partial<User>, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await userClient.update(user);
      dispatch(setMe({user: data}));
      return data;
    } catch (err) {
      return rejectWithValue(err.response.statusText);
    }
  }
);

export const passwordReset = createAsyncThunk(
  'account/passwordReset',
  async ({ password, private_key_armored, public_key_armored }: Partial<User>, { rejectWithValue }) => {
    try {
      const res = await userClient.passwordReset(password, private_key_armored, public_key_armored);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response.statusText);
    }
  }
);

export const resetAccountPassword = createAsyncThunk(
  'account/forgotAccountPassword',
  async ({ email }: any, { rejectWithValue }) => {
    try {
      await recoveryClient.resetPassword(email);
      return email;
    } catch (err) {
      if (err.response.status !== 404) {
        return rejectWithValue(err.response.statusText);
      }
    }
  }
);

export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    draftAccount: (state, { payload }) => void(state.draftAccount = payload),
    invites: (state, { payload }) => void(state.invites = payload),
    teams: (state, { payload }) => void(state.teams = payload),
    me: (state, { payload }) => {
      const me = JSON.parse(JSON.stringify(state.me));

      if (payload?.user?.id  && window.heap) {
        window.heap.identify(payload.user.id);
        window.heap.addUserProperties({first_name: payload.user.first_name, last_name: payload.user.last_name});
      }

      return void(state.me = merge(me, payload));
    },
    invoices: (state, { payload }) => void(state.invoices = payload),
    refreshAvatar: (state) => void(state.me.user.avatarKey = uuid()),
  },
})

export const {
  invites: setInvites,
  draftAccount: setDraftAccount,
  teams: setTeams,
  me: setMe,
  invoices: setInvoices,
  refreshAvatar,
} = accountSlice.actions;

export const selectAccountInvites = (state: AppState) => state.account.invites;
export const selectAccountTeams = (state: AppState) => state.account.teams;
export const selectMe = (state: AppState) => state.account.me;
export const selectInvoices = (state: AppState) => state.account.invoices;
export const selectDraftAccount = (state: AppState) => state.account.draftAccount;
export const selectAvatarKey = (state: AppState) => state.account.me.user.avatarKey;
